<template>
  <div class="flex flex-col gap-4">
    <OverrideDefaultValueField
      v-slot="{ defaultInputBinds }"
      name="customTotalBasePrice"
      :label="t('totalBasePrice')"
      type="number"
      :default-value="getTotalBasePrice(booking.priceCalculation)"
      :formatted-default-value="formatCurrency(getTotalBasePrice(booking.priceCalculation))"
      :initial-overridden-value="
        getCustomTotalBasePrice(booking.priceCalculation) ??
        getTotalBasePrice(booking.priceCalculation)
      "
      :disabled="disabled"
    >
      <Input
        type="number"
        :max-fraction-digits="2"
        :min="0"
        adornment="€"
        v-bind="defaultInputBinds"
      />
    </OverrideDefaultValueField>

    <div v-if="hasChanged && !disabled" class="flex justify-end gap-2">
      <CVButton variant="default" :is-loading="isBusy" @click.prevent="onSubmit">
        {{ t('save') }}
      </CVButton>
      <CVButton variant="secondary" outline :disabled="isBusy" @click.prevent="onCancel">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getCustomTotalBasePrice, getTotalBasePrice } from '@/utils/pricing.utils';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { ref, watch } from 'vue';
import { cloneDeep } from 'lodash';
import OverrideDefaultValueField from '@/components/OverrideDefaultValueField.vue';
import Input from '@/components/Input.vue';
import { customTotalBasePriceSchema } from '@/validation/price-calculation.schema';
import type { Booking } from '@/entities/bookings/booking.entity';
import CVButton from '@/components/buttons/CVButton.vue';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';
import { PriceRecalculationMode } from '@carvia/ros-client-types';
import { formatCurrency } from '@/utils/format-numbers';
import { useFormHasChanged } from '@/hooks/use-form-has-changed';

const props = defineProps<{
  booking: Booking;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
}>();

const { t } = useI18n();

const { confirmAndSave, isBusy } = usePriceChangeConfirmation();

const {
  handleSubmit,
  values: formValues,
  setValues,
} = useForm({
  initialValues: {
    customTotalBasePrice: getCustomTotalBasePrice(props.booking.priceCalculation),
  },
  validationSchema: customTotalBasePriceSchema,
});

const unchangedValues = ref({
  customTotalBasePrice: getCustomTotalBasePrice(props.booking.priceCalculation),
});

const onCancel = () => {
  setValues({
    customTotalBasePrice: getCustomTotalBasePrice(props.booking.priceCalculation),
  });
  unchangedValues.value = {
    customTotalBasePrice: getCustomTotalBasePrice(props.booking.priceCalculation),
  };
};

const onSubmit = handleSubmit(async (values) => {
  const { status } = await confirmAndSave({
    booking: props.booking,
    updateBookingDto: {
      pricing: {
        customTotalBasePrice: values.customTotalBasePrice ?? null,
        priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
      },
    },
  });
  if (status !== 'saved') return;
  unchangedValues.value = cloneDeep(formValues);
});

const hasChanged = useFormHasChanged(unchangedValues, formValues);
watch(hasChanged, () => emit('hasChanged', hasChanged.value));
</script>

<i18n lang="json">
{
  "en": {
    "totalBasePrice": "Car price (without extras)"
  },
  "de": {
    "totalBasePrice": "Fahrzeugpreis (ohne Extras)"
  },
  "es": {
    "totalBasePrice": "Precio del vehículo (sin extras)"
  }
}
</i18n>
